import type {
  CompositionV2DataQuery,
  PostingsDataQuery,
} from '@revelio/data-access';

import {
  DataProviderQueryStatus,
  DataType,
  type SingleDataSet,
  type TimeseriesData,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../types';
import {
  getLatestYoYGrowth,
  getYoYGrowthAt,
  inferYoYPeriodOffsetFromDateRefs,
} from '../utils/salary-growth-yoy';
import {
  calculateMarketTightnessMeterValue,
  calculateMeterValue,
} from './z-score/calculate-meter-value';

type CompositionV2 = NonNullable<CompositionV2DataQuery['composition_v2']>;
type CompositionEntity = NonNullable<CompositionV2['entities']>[number];
type PostingResponse = NonNullable<PostingsDataQuery['posting']>;

const buildSalaryValuesAlignedToMonths = (
  entity: CompositionEntity,
  monthsLength: number
): number[] => {
  const values = new Array(monthsLength).fill(0);
  const salaryData = entity.metrics?.salary ?? [];
  for (const point of salaryData) {
    const idx = point.id;
    if (typeof idx === 'number' && idx >= 0 && idx < monthsLength) {
      values[idx] = point.value ?? 0;
    }
  }
  return values;
};

const getSalaryGrowthForEntity = (
  entity: CompositionEntity,
  months: string[]
): number | null => {
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);
  const values = buildSalaryValuesAlignedToMonths(entity, months.length);
  const yoyGrowth = getLatestYoYGrowth(values, periodOffset);
  if (yoyGrowth !== null) {
    return yoyGrowth;
  }

  const salaryData = entity.metrics?.salary ?? [];
  if (salaryData.length < 2) {
    return null;
  }

  const firstValue = salaryData[0]?.value ?? 0;
  const lastValue = salaryData[salaryData.length - 1]?.value ?? 0;

  if (firstValue === 0) {
    return null;
  }

  return (lastValue - firstValue) / firstValue;
};

export function combineTwoQueryStatuses(
  statusA: DataProviderQueryStatus,
  statusB: DataProviderQueryStatus
): DataProviderQueryStatus {
  if (
    statusA === DataProviderQueryStatus.Success &&
    statusB === DataProviderQueryStatus.Success
  ) {
    return DataProviderQueryStatus.Success;
  }
  if (
    statusA === DataProviderQueryStatus.Error ||
    statusB === DataProviderQueryStatus.Error
  ) {
    return DataProviderQueryStatus.Error;
  }
  if (
    statusA === DataProviderQueryStatus.Fetching ||
    statusB === DataProviderQueryStatus.Fetching
  ) {
    return DataProviderQueryStatus.Fetching;
  }
  return DataProviderQueryStatus.Idle;
}

const computeDemandSupplyValue = (
  postings: number,
  headcount: number
): number => (headcount > 0 ? postings / headcount : 0);

export function computeDemandSupplyDataSet(
  composition: CompositionV2,
  posting: PostingResponse
): SingleDataSet {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];

  const headcountByEntityId = new Map<
    number,
    { headcount: number; dim: string; id?: number }
  >();
  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined) {
      continue;
    }
    const headcountData = entity.metrics?.headcount ?? [];
    const headcount =
      headcountData.length > 0
        ? (headcountData[headcountData.length - 1]?.value ?? 0)
        : 0;
    const dim =
      entity.metadata?.short_name ?? entity.metadata?.long_name ?? 'Selected';
    headcountByEntityId.set(id, { headcount, dim, id });
  }

  const activeByEntityId = new Map<number, number>();
  for (const entity of postingEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined || id === null) {
      continue;
    }
    const category = entity?.category;
    const active = category?.[category.length - 1]?.metrics?.active ?? 0;
    activeByEntityId.set(id, active);
  }

  const data: SingleDataSet['data'] = [];
  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined) {
      continue;
    }
    const headcountEntry = headcountByEntityId.get(id);
    const active = activeByEntityId.get(id);
    if (headcountEntry === undefined || active === undefined) {
      continue;
    }

    const value = computeDemandSupplyValue(active, headcountEntry.headcount);

    data.push({
      dim: headcountEntry.dim,
      value,
      ...(headcountEntry.id !== undefined && { id: String(headcountEntry.id) }),
    });
  }

  return { type: DataType.SINGLE, data };
}

function activeByMonthFromPostingEntity(
  category: NonNullable<PostingResponse[number]>['category']
): Map<string, number> {
  const byMonth = new Map<string, { active: number; date: string }>();
  if (!category) {
    return new Map<string, number>();
  }

  for (const timePoint of category) {
    const shortName = timePoint?.metadata?.shortName;
    if (!shortName) {
      continue;
    }
    const monthKey = shortName.slice(0, 7);
    if (monthKey.length !== 7) {
      continue;
    }

    const active = timePoint?.metrics?.active ?? 0;
    const current = byMonth.get(monthKey);
    if (current === undefined || current.date < shortName) {
      byMonth.set(monthKey, { active, date: shortName });
    }
  }

  return new Map(
    Array.from(byMonth.entries(), ([monthKey, data]) => [monthKey, data.active])
  );
}

export function hasDemandSupplyTimeseriesData(
  composition: CompositionV2,
  posting: PostingResponse
): boolean {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];
  if (compositionEntities.length === 0) {
    return false;
  }

  const months = composition.refs?.months;
  if (!months || months.length <= 1) {
    return false;
  }

  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined) {
      continue;
    }
    const headcountData = entity.metrics?.headcount ?? [];
    if (headcountData.length <= 1) {
      continue;
    }
    const postingEntity = postingEntities.find((e) => e?.metadata?.id === id);
    const category = postingEntity?.category;
    if (!category || category.length <= 1) {
      continue;
    }
    return true;
  }
  return false;
}

export function computeDemandSupplyTimeseriesDataSet(
  composition: CompositionV2,
  posting: PostingResponse
): TimeseriesDataSet {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];
  const months = composition.refs?.months ?? [];

  const postingActiveByEntityAndMonth = new Map<number, Map<string, number>>();
  for (const entity of postingEntities) {
    if (entity === null || entity === undefined) {
      continue;
    }
    const id = entity.metadata?.id;
    if (id === undefined || id === null) {
      continue;
    }
    postingActiveByEntityAndMonth.set(
      id,
      activeByMonthFromPostingEntity(entity.category)
    );
  }

  const data: TimeseriesData[] = [];
  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined) {
      continue;
    }
    const dim =
      entity.metadata?.short_name ?? entity.metadata?.long_name ?? 'Selected';
    const headcountData = entity.metrics?.headcount ?? [];
    const activeByMonth = postingActiveByEntityAndMonth.get(id);
    if (!activeByMonth) {
      continue;
    }

    const timeseries: TimeseriesDataPoint[] = [];
    for (let i = 0; i < headcountData.length; i++) {
      const point = headcountData[i];
      const rawIndex = point?.id ?? i;
      const index =
        typeof rawIndex === 'number' &&
        rawIndex >= 0 &&
        rawIndex < months.length
          ? rawIndex
          : i;
      const date = months[index] ?? '';
      if (!date || !activeByMonth.has(date)) {
        continue;
      }
      const headcount = point?.value ?? 0;
      const active = activeByMonth.get(date) ?? 0;
      timeseries.push({
        date,
        value: computeDemandSupplyValue(active, headcount),
      });
    }
    if (timeseries.length === 0) {
      continue;
    }
    data.push({
      dim,
      timeseries,
      ...(entity.metadata?.id !== undefined && {
        id: String(entity.metadata.id),
      }),
    });
  }
  return { type: DataType.TIMESERIES, data };
}

export function computeDemandSupplyPercentileTimeseriesDataSet(
  composition: CompositionV2,
  posting: PostingResponse
): TimeseriesDataSet {
  const ratioDataSet = computeDemandSupplyTimeseriesDataSet(
    composition,
    posting
  );
  const percentileData: TimeseriesData[] = ratioDataSet.data.map((item) => ({
    ...item,
    timeseries: item.timeseries.map((pt) => {
      const percentile =
        calculateMeterValue({
          value: pt.value ?? 0,
          metric: 'demandSupply',
        }) ?? 0;
      return { ...pt, value: percentile };
    }),
  }));
  return { type: DataType.TIMESERIES, data: percentileData };
}

export function computeDemandSupplyDataSetOrTimeseries(
  composition: CompositionV2,
  posting: PostingResponse,
  variant: 'ratio' | 'percentile'
): SingleDataSet | TimeseriesDataSet {
  if (hasDemandSupplyTimeseriesData(composition, posting)) {
    const timeSeries =
      variant === 'percentile'
        ? computeDemandSupplyPercentileTimeseriesDataSet(composition, posting)
        : computeDemandSupplyTimeseriesDataSet(composition, posting);

    if (timeSeries.data.length > 0) {
      return timeSeries;
    }
  }

  return variant === 'percentile'
    ? computeDemandSupplyPercentileDataSet(composition, posting)
    : computeDemandSupplyDataSet(composition, posting);
}

export function computeDemandSupplyPercentileDataSet(
  composition: CompositionV2,
  posting: PostingResponse
): SingleDataSet {
  const ratioDataSet = computeDemandSupplyDataSet(composition, posting);
  const percentileData = ratioDataSet.data.map((item) => {
    const percentile =
      calculateMeterValue({
        value: item.value ?? 0,
        metric: 'demandSupply',
      }) ?? 0;
    return { ...item, value: percentile };
  });
  return { type: DataType.SINGLE, data: percentileData };
}

export function computeMarketTightnessDataSet(
  composition: CompositionV2,
  posting: PostingResponse
): SingleDataSet {
  const compositionEntities = composition.entities ?? [];
  const postingEntities = posting ?? [];
  const months = composition.refs?.months ?? [];

  const compositionByEntityId = new Map<
    number,
    {
      headcount: number;
      dim: string;
      id?: number;
      wageGrowth: number | null;
    }
  >();

  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined || id === null) {
      continue;
    }

    const metrics = entity.metrics;
    const headcountData = metrics?.headcount ?? [];
    const headcount =
      headcountData.length > 0
        ? (headcountData[headcountData.length - 1]?.value ?? 0)
        : 0;

    const wageGrowth = getSalaryGrowthForEntity(entity, months);

    const dim =
      entity.metadata?.short_name ?? entity.metadata?.long_name ?? 'Selected';

    compositionByEntityId.set(id, {
      headcount,
      dim,
      id,
      wageGrowth,
    });
  }

  const postingsByEntityId = new Map<
    number,
    { active: number; timeToFill: number }
  >();

  for (const entity of postingEntities) {
    if (!entity) {
      continue;
    }
    const id = entity.metadata?.id;
    if (id === undefined || id === null) {
      continue;
    }

    const category = entity.category;
    const lastCategory =
      category && category.length > 0
        ? category[category.length - 1]
        : undefined;
    const metrics = lastCategory?.metrics;

    const active = metrics?.active ?? 0;
    const timeToFill = metrics?.time_to_fill ?? 0;

    postingsByEntityId.set(id, { active, timeToFill });
  }

  const data: SingleDataSet['data'] = [];

  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined || id === null) {
      continue;
    }

    const compositionEntry = compositionByEntityId.get(id);
    const postingsEntry = postingsByEntityId.get(id);
    if (!compositionEntry || !postingsEntry) {
      continue;
    }

    const { headcount, wageGrowth } = compositionEntry;
    const { active, timeToFill } = postingsEntry;

    if (wageGrowth === null) {
      continue;
    }

    const demandSupply = computeDemandSupplyValue(active, headcount);

    const value = calculateMarketTightnessMeterValue({
      wageGrowth,
      demandSupply,
      timeToFill,
    });

    data.push({
      dim: compositionEntry.dim,
      value,
      ...(compositionEntry.id !== undefined && {
        id: String(compositionEntry.id),
      }),
    });
  }

  return { type: DataType.SINGLE, data };
}

export const convertCompositionToWagePressure = ({
  data,
  variant = 'value',
}: {
  data: NonNullable<CompositionV2DataQuery['composition_v2']>;
  variant?: 'value' | 'percentile';
}): SingleDataSet => {
  const compositionEntities = data.entities ?? [];
  const months = data.refs?.months ?? [];
  const resultData: SingleDataSet['data'] = [];

  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined) {
      continue;
    }

    const growthValue = getSalaryGrowthForEntity(entity, months);

    if (growthValue === null) {
      continue;
    }

    const value =
      variant === 'percentile'
        ? (calculateMeterValue({
            value: growthValue,
            metric: 'wageGrowth',
          }) ?? 0)
        : growthValue;

    const dim =
      entity.metadata?.short_name ?? entity.metadata?.long_name ?? 'Selected';

    resultData.push({
      dim,
      value,
      ...(id !== undefined && { id: String(id) }),
    });
  }

  return { type: DataType.SINGLE, data: resultData };
};

export function convertCompositionToSalaryGrowth({
  data,
  chartType,
}: {
  data: NonNullable<CompositionV2DataQuery['composition_v2']>;
  chartType: string;
}): SingleDataSet | TimeseriesDataSet {
  const months = data.refs?.months ?? [];
  const periodOffset = inferYoYPeriodOffsetFromDateRefs(months);

  if (chartType === 'line') {
    const compositionEntities = data.entities ?? [];
    const raw: TimeseriesData[] = [];

    for (const entity of compositionEntities) {
      const metadata = entity?.metadata;
      if (!metadata) {
        continue;
      }
      const dim = metadata.short_name ?? metadata.long_name ?? 'Selected';
      const values = buildSalaryValuesAlignedToMonths(entity, months.length);
      const lineValues: TimeseriesDataPoint[] = [];
      for (let idx = 0; idx < months.length; idx++) {
        const dateStr = months[idx] ?? '';
        if (!dateStr) {
          continue;
        }
        const growthValue = getYoYGrowthAt(values, idx, periodOffset);
        lineValues.push({ date: dateStr, value: growthValue });
      }
      if (
        lineValues.length === 0 ||
        lineValues.every((v) => v.value === null)
      ) {
        continue;
      }
      raw.push({
        dim,
        timeseries: lineValues,
        ...(metadata.type === 'DATA_KIND_COMPANY' && metadata.id !== undefined
          ? { id: String(metadata.id) }
          : {}),
      });
    }

    if (raw.length === 0) {
      return { type: DataType.TIMESERIES, data: [] };
    }

    const firstIndexWithValue = (() => {
      const len = raw[0]?.timeseries.length ?? 0;
      for (let i = 0; i < len; i++) {
        const hasValue = raw.some(
          (s) =>
            s.timeseries[i]?.value !== null &&
            s.timeseries[i]?.value !== undefined
        );
        if (hasValue) {
          return i;
        }
      }
      return 0;
    })();

    const trimmed: TimeseriesData[] = raw.map((s) => ({
      dim: s.dim,
      timeseries: s.timeseries.slice(firstIndexWithValue),
      ...(s.id !== undefined ? { id: s.id } : {}),
    }));

    return { type: DataType.TIMESERIES, data: trimmed };
  }

  const compositionEntities = data.entities ?? [];
  const resultData: SingleDataSet['data'] = [];

  for (const entity of compositionEntities) {
    const id = entity?.metadata?.id;
    if (id === undefined) {
      continue;
    }

    const values = buildSalaryValuesAlignedToMonths(entity, months.length);
    const growthValue = getLatestYoYGrowth(values, periodOffset);
    const dim =
      entity.metadata?.short_name ?? entity.metadata?.long_name ?? 'Selected';

    resultData.push({
      dim,
      value: growthValue,
      ...(id !== undefined && { id: String(id) }),
    });
  }

  return { type: DataType.SINGLE, data: resultData };
}
